import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users, Building, Settings, BarChart3, Crown, Shield, Globe,
  Plus, Edit, Trash2, Eye, CheckCircle, XCircle, Clock, AlertTriangle,
  Search, Filter, Download, RefreshCw, Calendar, DollarSign, Target,
  Monitor, Database, Server, Activity, Palette, Code, Layout
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';

// Super Admin Dashboard Component
export const SuperAdminDashboard = ({ 
  dashboardStats, 
  onRefresh, 
  onNavigateToTenants, 
  onNavigateToUsers,
  loading = false 
}) => {
  const { t } = useTranslation();

  const getHealthBadgeColor = (health) => {
    switch (health) {
      case 'healthy': return 'bg-green-100 text-green-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const statsCards = [
    {
      title: t('superAdmin.overview.totalTenants'),
      value: dashboardStats?.total_tenants || 0,
      icon: Building,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: t('superAdmin.overview.activeTenants'),
      value: dashboardStats?.active_tenants || 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: t('superAdmin.overview.totalUsers'),
      value: dashboardStats?.total_users_across_tenants || 0,
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: t('superAdmin.overview.totalRevenue'),
      value: `$${(dashboardStats?.total_revenue_across_tenants || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Crown className="h-8 w-8 mr-3 text-yellow-600" />
            {t('superAdmin.title')}
          </h1>
          <p className="text-gray-600 mt-2">{t('superAdmin.overview.systemOverview')}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge className={getHealthBadgeColor(dashboardStats?.system_health)}>
            {t('superAdmin.overview.systemHealth')}: {dashboardStats?.system_health || 'unknown'}
          </Badge>
          <Button onClick={onRefresh} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('analytics.refresh')}
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-full ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenant Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building className="h-5 w-5 mr-2 text-mg-green" />
              Tenant Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Active</span>
              <Badge className="bg-green-100 text-green-800">
                {dashboardStats?.active_tenants || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Trial</span>
              <Badge className="bg-blue-100 text-blue-800">
                {dashboardStats?.trial_tenants || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Suspended</span>
              <Badge className="bg-red-100 text-red-800">
                {dashboardStats?.suspended_tenants || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-mg-green" />
              Monthly Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">New Tenants</span>
              <Badge className="bg-green-100 text-green-800">
                +{dashboardStats?.monthly_new_tenants || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Churned</span>
              <Badge className="bg-red-100 text-red-800">
                -{dashboardStats?.monthly_churned_tenants || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Tenants by Plan */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Target className="h-5 w-5 mr-2 text-mg-green" />
              Plans Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(dashboardStats?.tenants_by_plan || {}).map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 capitalize">{plan}</span>
                <Badge className="bg-gray-100 text-gray-800">{count}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common administrative tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Button onClick={onNavigateToTenants} className="bg-mg-green hover:bg-mg-green/90">
              <Building className="h-4 w-4 mr-2" />
              Manage Tenants
            </Button>
            <Button onClick={onNavigateToUsers} variant="outline">
              <Users className="h-4 w-4 mr-2" />
              View All Users
            </Button>
            <Button variant="outline">
              <Database className="h-4 w-4 mr-2" />
              System Backup
            </Button>
            <Button variant="outline">
              <Monitor className="h-4 w-4 mr-2" />
              System Logs
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Tenant Management Component
export const TenantManagement = ({ 
  tenants, 
  onCreateTenant, 
  onEditTenant, 
  onDeleteTenant,
  onViewTenant,
  onManageBranding,
  loading = false,
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  planFilter,
  onPlanFilterChange 
}) => {
  const { t } = useTranslation();

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Active': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'Inactive': { color: 'bg-gray-100 text-gray-800', icon: XCircle },
      'Suspended': { color: 'bg-red-100 text-red-800', icon: XCircle },
      'Trial': { color: 'bg-blue-100 text-blue-800', icon: Clock }
    };
    
    const config = statusConfig[status] || statusConfig['Inactive'];
    const IconComponent = config.icon;
    
    return (
      <Badge className={config.color}>
        <IconComponent className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  const getPlanBadge = (plan) => {
    const planColors = {
      'basic': 'bg-gray-100 text-gray-800',
      'premium': 'bg-blue-100 text-blue-800',
      'enterprise': 'bg-purple-100 text-purple-800'
    };
    
    return (
      <Badge className={planColors[plan] || planColors['basic']}>
        {plan.charAt(0).toUpperCase() + plan.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building className="h-6 w-6 mr-3 text-mg-green" />
            {t('superAdmin.tenants.title')}
          </h2>
          <p className="text-gray-600 mt-1">Manage all tenant organizations</p>
        </div>
        <Button onClick={onCreateTenant} className="bg-mg-green hover:bg-mg-green/90">
          <Plus className="h-4 w-4 mr-2" />
          {t('superAdmin.tenants.createTenant')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  placeholder="Search tenants..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Trial">Trial</SelectItem>
                <SelectItem value="Suspended">Suspended</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={planFilter} onValueChange={onPlanFilterChange}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {tenants?.map((tenant) => (
          <Card key={tenant.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg">{tenant.name}</CardTitle>
                  <CardDescription className="flex items-center mt-1">
                    <Globe className="h-3 w-3 mr-1" />
                    {tenant.subdomain}.crm.com
                  </CardDescription>
                </div>
                {getStatusBadge(tenant.status)}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Plan:</span>
                  {getPlanBadge(tenant.plan)}
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Users:</span>
                  <span className="font-medium">
                    {tenant.current_users || 0} / {tenant.max_users}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Policies:</span>
                  <span className="font-medium">{tenant.current_policies || 0}</span>
                </div>
                
                {tenant.is_trial && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Trial Ends:</span>
                    <span className="font-medium text-orange-600">
                      {new Date(tenant.trial_ends_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Created:</span>
                  <span className="font-medium">
                    {new Date(tenant.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                <Button size="sm" variant="outline" onClick={() => onViewTenant(tenant)}>
                  <Eye className="h-3 w-3 mr-1" />
                  View
                </Button>
                <Button size="sm" variant="outline" onClick={() => onEditTenant(tenant)}>
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => onManageBranding(tenant)}>
                  <Palette className="h-3 w-3 mr-1" />
                  Brand
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => onDeleteTenant(tenant)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading && (
        <div className="flex justify-center items-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-mg-green" />
        </div>
      )}

      {!loading && tenants?.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tenants found</h3>
            <p className="text-gray-600 mb-4">Get started by creating your first tenant organization.</p>
            <Button onClick={onCreateTenant} className="bg-mg-green hover:bg-mg-green/90">
              <Plus className="h-4 w-4 mr-2" />
              Create First Tenant
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Tenant Form Component (Create/Edit)
export const TenantForm = ({ 
  tenant = null, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: tenant?.name || '',
    subdomain: tenant?.subdomain || '',
    admin_email: tenant?.admin_email || '',
    contact_phone: tenant?.contact_phone || '',
    address: tenant?.address || '',
    plan: tenant?.plan || 'basic',
    max_users: tenant?.max_users || 10,
    max_policies: tenant?.max_policies || null,
    features_enabled: tenant?.features_enabled || ['leads', 'quotes', 'policies'],
    branding_scope: tenant?.branding_scope || 'Limited',
    is_trial: tenant?.is_trial || false,
    trial_days: 30
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      features_enabled: prev.features_enabled.includes(feature)
        ? prev.features_enabled.filter(f => f !== feature)
        : [...prev.features_enabled, feature]
    }));
  };

  const availableFeatures = [
    'leads', 'quotes', 'policies', 'renewals', 'tasks', 
    'commissions', 'integrations', 'communications', 'analytics'
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">{t('superAdmin.tenants.companyName')} *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              placeholder="Acme Insurance Corp"
            />
          </div>
          
          <div>
            <Label htmlFor="subdomain">{t('superAdmin.tenants.subdomain')} *</Label>
            <div className="flex items-center">
              <Input
                id="subdomain"
                value={formData.subdomain}
                onChange={(e) => setFormData(prev => ({ ...prev, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                required
                placeholder="acme-insurance"
                className="rounded-r-none"
              />
              <div className="px-3 py-2 bg-gray-100 border border-l-0 rounded-r-md text-sm text-gray-600">
                .crm.com
              </div>
            </div>
          </div>
          
          <div>
            <Label htmlFor="admin_email">{t('superAdmin.tenants.adminEmail')} *</Label>
            <Input
              id="admin_email"
              type="email"
              value={formData.admin_email}
              onChange={(e) => setFormData(prev => ({ ...prev, admin_email: e.target.value }))}
              required
              placeholder="admin@acme-insurance.com"
            />
          </div>
          
          <div>
            <Label htmlFor="contact_phone">{t('superAdmin.tenants.contactPhone')}</Label>
            <Input
              id="contact_phone"
              value={formData.contact_phone}
              onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>

        {/* Configuration */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="plan">{t('superAdmin.tenants.plan')}</Label>
            <Select 
              value={formData.plan} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, plan: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="max_users">{t('superAdmin.tenants.maxUsers')}</Label>
            <Input
              id="max_users"
              type="number"
              value={formData.max_users}
              onChange={(e) => setFormData(prev => ({ ...prev, max_users: parseInt(e.target.value) }))}
              min="1"
            />
          </div>
          
          <div>
            <Label htmlFor="branding_scope">{t('superAdmin.tenants.brandingScope')}</Label>
            <Select 
              value={formData.branding_scope} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, branding_scope: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Theme Only">Theme Colors Only</SelectItem>
                <SelectItem value="Limited">Limited Branding</SelectItem>
                <SelectItem value="Full">Full Branding Control</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_trial"
              checked={formData.is_trial}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_trial: checked }))}
            />
            <Label htmlFor="is_trial">{t('superAdmin.tenants.isTrial')}</Label>
          </div>
          
          {formData.is_trial && (
            <div>
              <Label htmlFor="trial_days">{t('superAdmin.tenants.trialDays')}</Label>
              <Input
                id="trial_days"
                type="number"
                value={formData.trial_days}
                onChange={(e) => setFormData(prev => ({ ...prev, trial_days: parseInt(e.target.value) }))}
                min="1"
                max="365"
              />
            </div>
          )}
        </div>
      </div>

      {/* Features */}
      <div>
        <Label className="text-base font-medium">{t('superAdmin.tenants.featuresEnabled')}</Label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {availableFeatures.map(feature => (
            <div key={feature} className="flex items-center space-x-2">
              <Switch
                id={feature}
                checked={formData.features_enabled.includes(feature)}
                onCheckedChange={() => handleFeatureToggle(feature)}
              />
              <Label htmlFor={feature} className="capitalize">{feature}</Label>
            </div>
          ))}
        </div>
      </div>

      {/* Address */}
      <div>
        <Label htmlFor="address">{t('superAdmin.tenants.address')}</Label>
        <Textarea
          id="address"
          value={formData.address}
          onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
          placeholder="123 Business Ave, Suite 100, City, State 12345"
          rows={3}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4 pt-6 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={loading} className="bg-mg-green hover:bg-mg-green/90">
          {loading ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : tenant ? (
            <Edit className="h-4 w-4 mr-2" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          {loading ? 'Saving...' : tenant ? 'Update Tenant' : 'Create Tenant'}
        </Button>
      </div>
    </form>
  );
};

// White Label Configuration Component  
export const WhiteLabelConfig = ({ 
  tenants, 
  selectedTenant, 
  onTenantSelect,
  brandingConfig,
  onUpdateBranding,
  onPreviewBranding,
  loading = false
}) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState(brandingConfig || {});
  const [activeTab, setActiveTab] = useState('company');

  useEffect(() => {
    if (brandingConfig) {
      setConfig(brandingConfig);
    }
  }, [brandingConfig]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdateBranding(config);
  };

  const handlePreview = () => {
    onPreviewBranding(config);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Palette className="h-6 w-6 mr-3 text-mg-green" />
            {t('superAdmin.whiteLabelConfig.title')}
          </h2>
          <p className="text-gray-600 mt-1">Configure white label branding for tenants</p>
        </div>
        <div className="flex items-center space-x-4">
          <Button onClick={handlePreview} variant="outline" disabled={!selectedTenant}>
            <Eye className="h-4 w-4 mr-2" />
            {t('superAdmin.whiteLabelConfig.previewBranding')}
          </Button>
        </div>
      </div>

      {/* Tenant Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('superAdmin.whiteLabelConfig.selectTenant')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedTenant?.id || ''} onValueChange={onTenantSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Select a tenant to configure" />
            </SelectTrigger>
            <SelectContent>
              {tenants?.map(tenant => (
                <SelectItem key={tenant.id} value={tenant.id}>
                  {tenant.name} ({tenant.subdomain})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedTenant && (
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>{t('superAdmin.whiteLabelConfig.brandingConfiguration')}</CardTitle>
              <CardDescription>
                Configure branding for {selectedTenant.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="company">Company</TabsTrigger>
                  <TabsTrigger value="colors">Colors</TabsTrigger>
                  <TabsTrigger value="assets">Assets</TabsTrigger>
                  <TabsTrigger value="advanced">Advanced</TabsTrigger>
                </TabsList>

                {/* Company Tab */}
                <TabsContent value="company" className="space-y-4 mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company_name">{t('superAdmin.whiteLabelConfig.companyName')}</Label>
                      <Input
                        id="company_name"
                        value={config.company_name || ''}
                        onChange={(e) => setConfig(prev => ({...prev, company_name: e.target.value}))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="company_tagline">{t('superAdmin.whiteLabelConfig.companyTagline')}</Label>
                      <Input
                        id="company_tagline"
                        value={config.company_tagline || ''}
                        onChange={(e) => setConfig(prev => ({...prev, company_tagline: e.target.value}))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="company_phone">{t('superAdmin.whiteLabelConfig.companyPhone')}</Label>
                      <Input
                        id="company_phone"
                        value={config.company_phone || ''}
                        onChange={(e) => setConfig(prev => ({...prev, company_phone: e.target.value}))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="company_email">{t('superAdmin.whiteLabelConfig.companyEmail')}</Label>
                      <Input
                        id="company_email"
                        type="email"
                        value={config.company_email || ''}
                        onChange={(e) => setConfig(prev => ({...prev, company_email: e.target.value}))}
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Colors Tab */}
                <TabsContent value="colors" className="space-y-4 mt-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="primary_color">{t('superAdmin.whiteLabelConfig.primaryColor')}</Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          type="color"
                          value={config.primary_color || '#006400'}
                          onChange={(e) => setConfig(prev => ({...prev, primary_color: e.target.value}))}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={config.primary_color || '#006400'}
                          onChange={(e) => setConfig(prev => ({...prev, primary_color: e.target.value}))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="accent_color">{t('superAdmin.whiteLabelConfig.accentColor')}</Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          type="color"
                          value={config.accent_color || '#FFD700'}
                          onChange={(e) => setConfig(prev => ({...prev, accent_color: e.target.value}))}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={config.accent_color || '#FFD700'}
                          onChange={(e) => setConfig(prev => ({...prev, accent_color: e.target.value}))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Assets Tab */}
                <TabsContent value="assets" className="space-y-4 mt-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="logo_url">{t('superAdmin.whiteLabelConfig.logoUrl')}</Label>
                      <Input
                        id="logo_url"
                        value={config.logo_url || ''}
                        onChange={(e) => setConfig(prev => ({...prev, logo_url: e.target.value}))}
                        placeholder="https://example.com/logo.png"
                      />
                    </div>
                    <div>
                      <Label htmlFor="favicon_url">{t('superAdmin.whiteLabelConfig.faviconUrl')}</Label>
                      <Input
                        id="favicon_url"
                        value={config.favicon_url || ''}
                        onChange={(e) => setConfig(prev => ({...prev, favicon_url: e.target.value}))}
                        placeholder="https://example.com/favicon.ico"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Advanced Tab */}
                <TabsContent value="advanced" className="space-y-4 mt-6">
                  {selectedTenant.branding_scope === 'Full' ? (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="custom_css">{t('superAdmin.whiteLabelConfig.customCss')}</Label>
                        <Textarea
                          id="custom_css"
                          value={config.custom_css || ''}
                          onChange={(e) => setConfig(prev => ({...prev, custom_css: e.target.value}))}
                          rows={6}
                          placeholder="/* Custom CSS styles */"
                          className="font-mono text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="header_html">{t('superAdmin.whiteLabelConfig.headerHtml')}</Label>
                        <Textarea
                          id="header_html"
                          value={config.header_html || ''}
                          onChange={(e) => setConfig(prev => ({...prev, header_html: e.target.value}))}
                          rows={4}
                          placeholder="<div>Custom header content</div>"
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                  ) : (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        Advanced branding options are only available for tenants with 'Full' branding scope.
                        Current scope: {selectedTenant.branding_scope}
                      </AlertDescription>
                    </Alert>
                  )}
                </TabsContent>
              </Tabs>

              <div className="flex justify-end space-x-4 pt-6 border-t mt-6">
                <Button type="button" variant="outline" onClick={() => setConfig(brandingConfig || {})}>
                  {t('superAdmin.whiteLabelConfig.resetToDefault')}
                </Button>
                <Button type="submit" disabled={loading} className="bg-mg-green hover:bg-mg-green/90">
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Palette className="h-4 w-4 mr-2" />
                  )}
                  {loading ? 'Saving...' : t('superAdmin.whiteLabelConfig.applyBranding')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      )}
    </div>
  );
};

// System Users Management Component
export const SystemUsersManagement = ({ 
  users, 
  tenants, 
  onCreateUser, 
  onEditUser, 
  onDeleteUser,
  loading = false,
  searchTerm,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  tenantFilter,
  onTenantFilterChange
}) => {
  const { t } = useTranslation();

  const getRoleBadge = (role) => {
    const roleColors = {
      'Super Admin': 'bg-yellow-100 text-yellow-800',
      'Admin': 'bg-red-100 text-red-800',
      'Manager': 'bg-blue-100 text-blue-800',
      'Agente': 'bg-green-100 text-green-800',
      'CSR': 'bg-purple-100 text-purple-800',
      'Auditor': 'bg-gray-100 text-gray-800'
    };
    
    return (
      <Badge className={roleColors[role] || roleColors['Agente']}>
        {role === 'Super Admin' && <Crown className="h-3 w-3 mr-1" />}
        {role === 'Admin' && <Shield className="h-3 w-3 mr-1" />}
        {t(`superAdmin.userRoles.${role.toLowerCase().replace(' ', '')}`) || role}
      </Badge>
    );
  };

  const getTenantName = (tenantId) => {
    if (!tenantId) return 'System Wide';
    const tenant = tenants?.find(t => t.id === tenantId);
    return tenant ? tenant.name : 'Unknown Tenant';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Users className="h-6 w-6 mr-3 text-mg-green" />
            {t('superAdmin.systemManagement.allUsers')}
          </h2>
          <p className="text-gray-600 mt-1">Manage all users across the system</p>
        </div>
        <Button onClick={onCreateUser} className="bg-mg-green hover:bg-mg-green/90">
          <Plus className="h-4 w-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={roleFilter} onValueChange={onRoleFilterChange}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="Super Admin">Super Admin</SelectItem>
                <SelectItem value="Admin">Admin</SelectItem>
                <SelectItem value="Manager">Manager</SelectItem>
                <SelectItem value="Agente">Agent</SelectItem>
                <SelectItem value="CSR">CSR</SelectItem>
                <SelectItem value="Auditor">Auditor</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tenantFilter} onValueChange={onTenantFilterChange}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by tenant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tenants</SelectItem>
                <SelectItem value="system">System Wide</SelectItem>
                {tenants?.map(tenant => (
                  <SelectItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users?.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.full_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                        <div className="text-xs text-gray-400">
                          @{user.username}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getTenantName(user.tenant_id)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge className={user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <Button size="sm" variant="outline" onClick={() => onEditUser(user)}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => onDeleteUser(user)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex justify-center items-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-mg-green" />
        </div>
      )}

      {!loading && users?.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600">No users match your current filter criteria.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};